import { useSelector } from "redux/hooks";
import { FormattedMessage } from "react-intl";
import classNames from "classnames";
import styled from "styled-components";

// Components
import AnimatedModal from "components/AnimateModal/AnimateModal";
import CustomButton from "components/Button";

// Styles
import styles from "./UpgradePlan.module.scss";

// Icons
import { CheckBoxIcon, ArrowIcon } from "./Icons";

const DurationButton = styled(CustomButton)`
  background-color: #5046E6;
  color: #fff;
  flex-shrink: 0;
  font-size: 16px;
  line-height: 15px;
  font-weight: 600;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  font-family: "inter";
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.25);
  width: 100%;
  
  @media (max-width: 576px) {
    font-size: 11px;
    padding: 5px 12px;
  }
`;

interface UpgradeMemberModelProps {
    openUpgradeModel: boolean;
    onClose: () => void;
}

export const UpgradeMemberModel = ({ openUpgradeModel, onClose }: UpgradeMemberModelProps) => {
    const { theme, userDetail } = useSelector((state) => state.authReducer);
    const organizationName = userDetail?.user?.team?.name;
    const planName = userDetail?.user?.activeSubscription?.name;

    const userName = organizationName?.replace(/'s (team|organization)$/i, '');
    const handleContactAdmin = () => {
        // Create email with prefilled data
        const subject = encodeURIComponent(`DeftGPT Credit Limit Reached for ${organizationName}`);
        const body = encodeURIComponent(`Hi ${userName},

Our organization has reached the credit limit for DeftGPT ${planName}. Could you please purchase additional credits or upgrade our plan?

You can manage our current plan here: https://deftgpt.com/settings/current-plan

This message was sent from DeftGPT.`);
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
    };

    return (
        <AnimatedModal
            upgradeMember={true}
            isOpen={openUpgradeModel}
            onClose={onClose}
        >
            <div className={styles.container1}>
                <div className={styles.topBorder}></div>
                <div className={styles.section1}>
                    <div className="flex gap-[8px] items-center">
                        <div className={styles.icon}><img
                                src={require("./upgradeIcon.png")}
                                alt="Incognito Icon"
                                className="w-6 h-6"
                            /></div>
                        <h2
                            className={classNames("font-inter", styles.mainHead, {
                                [styles.light]: theme === "light",
                                [styles.dark]: theme === "dark"
                            })}
                        >
                            {userDetail?.user?.name}, <FormattedMessage id="upgrade.member.head" />
                        </h2>
                    </div>
                    <div
                        className={classNames("font-inter", styles.subtitle, {
                            [styles.light]: theme === "light",
                            [styles.dark]: theme === "dark"
                        })}
                    >
                        <FormattedMessage id="upgrade.member.subtitle" />
                    </div>
                </div>
                <div className={styles.container2}>
                    <div className={styles.planBox}>
                        <ul className="flex gap-[8px] flex-col w-full font-inter">
                            <li className={styles.features}>
                               <span className="flex-shrink-0 leading-[0]"><CheckBoxIcon /></span> 
                                <span className={`${styles.statements} ${styles.mainStat}`}>
                                    <div className="w-[148px]"><FormattedMessage id="organisation.admin" /></div>
                                    <span className={styles.highlightedTxt}>{userName}</span>
                                </span>
                            </li>
                        </ul>
                    </div>
                    <div className="flex gap-[12px] flex-col w-full items-center font-inter">
                        <DurationButton onClick={handleContactAdmin}>
                            <span className={`font-inter ${styles.btn}`}>
                                <FormattedMessage id="contact.admin" />
                                <span className="leading-[0] flex-shrink-0"><ArrowIcon /></span>
                            </span>
                        </DurationButton>
                        <div
                            className={classNames("font-inter", styles.footer, {
                                [styles.light]: theme === "light",
                                [styles.dark]: theme === "dark"
                            })}
                        >
                            <FormattedMessage id="upgrade.member.model.footer" />
                        </div>
                    </div>
                </div>
            </div>
        </AnimatedModal>
    );
};
